import { useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient.js";
import { api } from "../lib/api.js";
import { TURNSTILE_SITE_KEY } from "../env.js";
import { Turnstile } from "./Turnstile.js";

type Status = "loading" | "signed-out" | "none" | "admin" | "customer";

interface MeResponse {
  role: "admin" | "customer" | "none";
  email: string;
  message?: string;
}

export interface ResolvedIdentity {
  role: "admin" | "customer";
  email: string;
}

/**
 * Gates the whole app behind Supabase auth + server-resolved role. Having a
 * session is NOT sufficient - `GET /api/me` independently re-resolves the role
 * server-side (functions/lib/role.ts) on every load, so this component never
 * trusts client-only auth state for anything beyond deciding what to render.
 */
export function AuthGate({ children }: { children: (me: ResolvedIdentity) => ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [notLinkedMessage, setNotLinkedMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function check(session: Session | null) {
      if (!session) {
        if (!cancelled) setStatus("signed-out");
        return;
      }
      try {
        const me = await api.get<MeResponse>("/me");
        if (cancelled) return;
        setEmail(me.email);
        if (me.role === "admin") setStatus("admin");
        else if (me.role === "customer") setStatus("customer");
        else {
          setNotLinkedMessage(me.message ?? "This email isn't linked to a licence — contact support@screenswytch.co.uk");
          setStatus("none");
        }
      } catch {
        if (!cancelled) setStatus("signed-out");
      }
    }

    supabase.auth.getSession().then(({ data }) => check(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => check(session));
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (status === "loading") return <div className="centered">Loading…</div>;
  if (status === "signed-out") return <LoginForm />;
  if (status === "none") {
    return (
      <div className="centered">
        <div className="auth-card">
          <img className="auth-logo" src="/logo.png" alt="" />
          <p className="auth-wordmark">ScreenSw<span className="y">y</span>tch</p>
          <p className="auth-sub" style={{ marginBottom: 18 }}>{notLinkedMessage}</p>
          <button className="secondary" onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </div>
    );
  }

  // Each shell (admin sidebar / customer top-bar) is rendered by the child now,
  // so it can own its own chrome; AuthGate just resolves identity.
  return <>{children({ role: status, email: email! })}</>;
}

/** The customer portal (`account.`) uses passwordless magic-link sign-in; the
 * admin console (`console.`) uses Microsoft 365 (Azure) sign-in - never a
 * password, and never magic-link for admins. Hostname only decides which of
 * these two the sign-in screen offers; the server still resolves the actual
 * role from admin_users/customers, so the choice here is purely which sign-in
 * method to present. */
function isAccountHost(): boolean {
  return typeof window !== "undefined" && window.location.hostname.startsWith("account.");
}

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function LoginForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  // CAPTCHA (only active when a Turnstile site key is configured). captchaKey is
  // bumped to remount the widget for a fresh, single-use token after each submit.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const accountHost = isAccountHost();

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    setSubmitting(false);
    // The Turnstile token is single-use - reset the widget for any retry.
    setCaptchaToken(null);
    setCaptchaKey((k) => k + 1);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    setSent(true);
  }

  async function handleMicrosoft() {
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: { redirectTo: window.location.origin, scopes: "email openid profile" },
    });
    if (oauthError) setError(oauthError.message);
  }

  if (sent) {
    return (
      <div className="centered">
        <div className="auth-card">
          <img className="auth-logo" src="/logo.png" alt="" />
          <p className="auth-wordmark">ScreenSw<span className="y">y</span>tch</p>
          <p className="auth-sub" style={{ marginBottom: 6 }}>Check your email</p>
          <p style={{ color: "var(--muted)", fontSize: "0.9rem", margin: 0 }}>
            We've sent a secure sign-in link to <strong style={{ color: "var(--ink)" }}>{email}</strong>. It's single-use and expires shortly.
          </p>
        </div>
      </div>
    );
  }

  const emailForm = (
    <form onSubmit={handleMagicLink} className="login-form">
      <label htmlFor="email">
        Email address
        <input
          id="email"
          type="email"
          required
          maxLength={320}
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      {TURNSTILE_SITE_KEY && (
        <Turnstile key={captchaKey} siteKey={TURNSTILE_SITE_KEY} onToken={setCaptchaToken} />
      )}
      <button
        type="submit"
        disabled={submitting || email.length === 0 || (!!TURNSTILE_SITE_KEY && !captchaToken)}
      >
        {submitting ? "Sending…" : "Email me a sign-in link"}
      </button>
    </form>
  );

  return (
    <div className="centered">
      <div className="auth-card">
        <img className="auth-logo" src="/logo.png" alt="" />
        <p className="auth-wordmark">ScreenSw<span className="y">y</span>tch</p>
        <p className="auth-sub">{accountHost ? "Manage your licence" : "Admin console"}</p>

        {accountHost ? (
          emailForm
        ) : (
          <>
            <button className="btn-ms" onClick={handleMicrosoft} type="button">
              <MicrosoftLogo />
              Sign in with Microsoft
            </button>
            {!showEmailFallback ? (
              <p className="auth-alt">
                <button type="button" onClick={() => setShowEmailFallback(true)}>Trouble signing in? Use an email link</button>
              </p>
            ) : (
              <div style={{ marginTop: 14 }}>{emailForm}</div>
            )}
          </>
        )}

        {error && <p className="error-text" style={{ marginTop: 12 }}>{error}</p>}
      </div>
    </div>
  );
}
